import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import EmployeesPicker, { useOutsideScopeIds } from "@/components/EmployeesPicker";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import { useFilters } from "@/hooks/useFilters";
import reports from "@/api/reports";
import { TX_COLORS, colorFor } from "@/lib/reportColors";

const MAX_EMPS = 10;

export default function Comparativo() {
  const { t } = useTranslation();
  const { filters, setFilters, reset, payload } = useFilters();
  const [sp, setSp] = useSearchParams();
  const initial = (sp.get("compare") || "").split(",").filter(Boolean);
  const [picked, setPicked] = useState(initial);
  const [data, setData] = useState({ employees: [], transaction_types: [] });

  // Stable keys for `payload` and `picked` (objects/arrays change identity on
  // every render even when the underlying values are unchanged).
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);
  const pickedKey = useMemo(() => picked.join(","), [picked]);

  useEffect(() => {
    if (picked.length === 0) {
      setData({ employees: [], transaction_types: [] });
      return;
    }
    reports.comparativo(payload, picked).then((r) => setData(r.data || { employees: [], transaction_types: [] }));
    // payload + picked tracked indirectly via stable keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, pickedKey]);

  // Sync picked → querystring (kept independent of GlobalFilters).
  useEffect(() => {
    const next = new URLSearchParams(sp);
    if (picked.length) next.set("compare", picked.join(","));
    else next.delete("compare");
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedKey]);

  const chartData = useMemo(() => {
    const types = data.transaction_types || [];
    return (data.employees || []).map((e) => {
      const row = { employee_name: e.employee_name };
      types.forEach((tt) => (row[tt] = e.by_type?.[tt] || 0));
      return row;
    });
  }, [data]);

  const onPick = (next) => {
    if (next.length > MAX_EMPS) return;
    setPicked(next);
  };

  return (
    <div className="space-y-6" data-testid="comparativo-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="comparativo-title">
          {t("comparativo.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("comparativo.subtitle")}</p>
      </div>

      <GlobalFilters filters={filters} onChange={setFilters} onReset={reset} />

      <div className="space-y-1.5">
        <Label className="text-xs">{t("comparativo.pickEmployees")}</Label>
        <EmployeesPicker
          value={picked}
          onChange={onPick}
          sourceReport={filters.source_report || undefined}
          max={MAX_EMPS}
          placeholder={t("comparativo.pickEmployees")}
          testId="comparativo-emp-picker"
        />
        {picked.length >= MAX_EMPS && (
          <Alert variant="default" className="mt-2" data-testid="comparativo-max-alert">
            <AlertDescription>{t("comparativo.max")}</AlertDescription>
          </Alert>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("comparativo.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div
              className="py-16 text-center text-sm text-muted-foreground"
              data-testid="comparativo-empty"
            >
              {picked.length === 0 ? t("comparativo.empty") : t("common.loading")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={chartData} margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="employee_name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <Legend />
                {(data.transaction_types || []).map((tt, i) => (
                  <Bar
                    key={tt}
                    dataKey={tt}
                    fill={TX_COLORS[tt] || colorFor(tt, i)}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
